import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const Contact22 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        enquiryType: 'general',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '', enquiryType: 'general' });
        }, 3000);
    };

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8DDD0',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#5C3D2E',
        outline: 'none',
        fontFamily: "'Source Sans 3', -apple-system, sans-serif",
        transition: 'border-color 0.3s ease',
    };

    const contactInfo = [
        {
            icon: Phone,
            title: 'Call or WhatsApp',
            detail: '+91 98765 43210',
            sub: 'Mon-Sat, 10am - 7pm IST',
            action: 'https://wa.me/919876543210',
            actionLabel: 'Chat on WhatsApp',
        },
        {
            icon: Mail,
            title: 'Email Us',
            detail: 'hello@maati.in',
            sub: 'We reply within 24 hours',
            action: 'mailto:hello@maati.in',
            actionLabel: 'Send Email',
        },
        {
            icon: MapPin,
            title: 'Visit Our Studio',
            detail: '42 Artisan Lane, Bandra West',
            sub: 'Mumbai, Maharashtra 400050',
            action: null,
            actionLabel: null,
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

                .maati-contact-input:focus {
                    border-color: #C4572A !important;
                }
            `}</style>
            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                {/* Hero Section */}
                <section style={{
                    padding: '120px 24px 60px', borderBottom: '1px solid #E8DDD0',
                    textAlign: 'center',
                }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                        <p style={{
                            fontSize: '12px', fontWeight: 500, color: '#C4572A',
                            letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '16px',
                        }}>
                            Get In Touch
                        </p>
                        <h1 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400,
                            color: '#5C3D2E', marginBottom: '16px', lineHeight: 1.1,
                        }}>
                            Contact Us
                        </h1>
                        <p style={{ fontSize: '16px', color: '#8B7355', lineHeight: 1.6 }}>
                            Whether you need craft consultation, custom orders, or have a question about your purchase, we are here to help.
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 24px 80px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px' }}>

                        {/* Contact Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                            {contactInfo.map((info, i) => (
                                <div key={i} style={{
                                    backgroundColor: '#FFFFFF', borderRadius: '12px',
                                    border: '1px solid #E8DDD0', padding: '28px',
                                    textAlign: 'center', transition: 'box-shadow 0.3s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(92,61,46,0.1)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '50%',
                                        backgroundColor: '#F3ECE4', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        margin: '0 auto 16px',
                                    }}>
                                        <info.icon size={24} color="#C4572A" />
                                    </div>
                                    <h3 style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '20px', fontWeight: 600, color: '#5C3D2E',
                                        margin: '0 0 8px 0',
                                    }}>
                                        {info.title}
                                    </h3>
                                    <p style={{ fontSize: '15px', fontWeight: 500, color: '#5C3D2E', margin: '0 0 4px 0' }}>
                                        {info.detail}
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#8B7355', margin: '0 0 16px 0' }}>
                                        {info.sub}
                                    </p>
                                    {info.action && (
                                        <a
                                            href={info.action}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                fontSize: '13px', fontWeight: 600, color: '#C4572A',
                                                textDecoration: 'none', transition: 'color 0.2s',
                                            }}
                                        >
                                            {info.actionLabel} <Send size={14} />
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Form Section */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px' }}>
                            <div style={{
                                backgroundColor: '#FFFFFF', borderRadius: '12px',
                                border: '1px solid #E8DDD0', padding: '40px',
                            }}>
                                <h2 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '28px', fontWeight: 600, color: '#5C3D2E',
                                    margin: '0 0 8px 0',
                                }}>
                                    Send Us a Message
                                </h2>
                                <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '28px' }}>
                                    Fill out the form below and we will get back to you within 24 hours.
                                </p>

                                {submitted ? (
                                    <div style={{
                                        textAlign: 'center', padding: '48px 24px',
                                        backgroundColor: '#F3ECE4', borderRadius: '10px',
                                    }}>
                                        <div style={{
                                            width: '60px', height: '60px', borderRadius: '50%',
                                            backgroundColor: '#E8F5E9', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                                        }}>
                                            <Send size={24} color="#2E7D32" />
                                        </div>
                                        <h3 style={{
                                            fontFamily: "'Cormorant Infant', serif",
                                            fontSize: '24px', fontWeight: 600, color: '#5C3D2E', marginBottom: '8px',
                                        }}>
                                            Message Sent
                                        </h3>
                                        <p style={{ fontSize: '14px', color: '#8B7355' }}>
                                            Thank you for reaching out. Our craft consultants will respond shortly.
                                        </p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit}>
                                        {/* Enquiry Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '8px' }}>
                                                Enquiry Type
                                            </label>
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                {[
                                                    { value: 'general', label: 'General' },
                                                    { value: 'custom', label: 'Custom Order' },
                                                    { value: 'consultation', label: 'Craft Consultation' },
                                                    { value: 'order', label: 'Order Query' },
                                                ].map(type => (
                                                    <button
                                                        key={type.value}
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, enquiryType: type.value })}
                                                        style={{
                                                            padding: '8px 18px', borderRadius: '20px',
                                                            fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                                                            fontFamily: "'Source Sans 3', sans-serif",
                                                            border: formData.enquiryType === type.value ? '1px solid #C4572A' : '1px solid #E8DDD0',
                                                            backgroundColor: formData.enquiryType === type.value ? '#FFF0EB' : 'transparent',
                                                            color: formData.enquiryType === type.value ? '#C4572A' : '#5C3D2E',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        {type.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '6px' }}>Name *</label>
                                                <input
                                                    type="text" name="name" required
                                                    value={formData.name} onChange={handleInputChange}
                                                    placeholder="Your name"
                                                    className="maati-contact-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '6px' }}>Email *</label>
                                                <input
                                                    type="email" name="email" required
                                                    value={formData.email} onChange={handleInputChange}
                                                    placeholder="your@email.com"
                                                    className="maati-contact-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '6px' }}>Phone</label>
                                                <input
                                                    type="tel" name="phone"
                                                    value={formData.phone} onChange={handleInputChange}
                                                    placeholder="+91 98765 43210"
                                                    className="maati-contact-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '6px' }}>Subject *</label>
                                                <input
                                                    type="text" name="subject" required
                                                    value={formData.subject} onChange={handleInputChange}
                                                    placeholder="What is this about?"
                                                    className="maati-contact-input"
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '24px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5C3D2E', marginBottom: '6px' }}>Message *</label>
                                            <textarea
                                                name="message" required rows="5"
                                                value={formData.message} onChange={handleInputChange}
                                                placeholder="Tell us about your requirement..."
                                                className="maati-contact-input"
                                                style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            style={{
                                                width: '100%', padding: '16px', backgroundColor: '#C4572A',
                                                color: '#FFFFFF', border: 'none', borderRadius: '8px',
                                                fontSize: '15px', fontWeight: 600, letterSpacing: '1px',
                                                textTransform: 'uppercase', cursor: 'pointer',
                                                fontFamily: "'Source Sans 3', sans-serif",
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4683B'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; }}
                                        >
                                            <Send size={18} /> Send Message
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* WhatsApp CTA */}
                        <div style={{
                            backgroundColor: '#FFFFFF', borderRadius: '12px',
                            border: '1px solid #E8DDD0', padding: '32px',
                            textAlign: 'center',
                        }}>
                            <MessageCircle size={32} color="#25D366" style={{ marginBottom: '12px' }} />
                            <h3 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '24px', fontWeight: 600, color: '#5C3D2E', marginBottom: '8px',
                            }}>
                                Prefer WhatsApp?
                            </h3>
                            <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px' }}>
                                Chat directly with our craft consultants for custom orders, fabric queries, or styling advice. We respond within minutes during business hours.
                            </p>
                            <a
                                href="https://wa.me/919876543210"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '14px 32px', backgroundColor: '#25D366', color: '#FFFFFF',
                                    borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                                    textDecoration: 'none', transition: 'all 0.3s ease',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                }}
                            >
                                <MessageCircle size={18} /> Chat on WhatsApp
                            </a>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Contact22;
